/**
 * Autorizações de manobra automática — Optimale Grid (frontend, US-2102, GUS-907, ADR-0004).
 *
 * Criado em: 19/10/2026 · Última modificação: 19/10/2026 · Versão: 1.0.0
 *
 * O motor só executa a ação `executar_manobra` de uma regra se houver autorização **vigente** para o atuador e a ação,
 * dada por supervisor ou coordenador da própria operadora (capacidade `autorizar_manobra_automatica`). A equipe
 * Optimale nunca autoriza nem aciona. A autorização tem validade; a lista mostra vigentes, vencidas e revogadas, as
 * regras que a usam e as execuções automáticas ligadas a ela. Todos que veem a operadora consultam.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi } from "../api";
import { quando } from "../admin/tipos";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { marcaAtual } from "../marca";

type Autorizacao = { id: string; atuador: string; acao: string; motivo: string; autorizado_por: string; criado_em: string;
  valido_ate: string | null; revogado_por: string | null; revogado_em: string | null; situacao: "vigente" | "vencida" | "revogada";
  execucoes: number; regras: { id: string; nome: string; ativa: boolean }[] };
type Execucao = { command_id: string; criado_em: string; status: string; motivo: string; regra_id: string | null };

const SITUACAO: Record<Autorizacao["situacao"], [string, string]> = { vigente: ["Vigente", "situacao-validado"],
  vencida: ["Vencida", "situacao-pendente"], revogada: ["Revogada", "situacao-rejeitado"] };
const ACOES = ["DESLIGAR_BOMBA", "LIGAR_BOMBA", "FECHAR_VALVULA", "ABRIR_VALVULA"];

export function ManobraAutomatica() {
  const { usuario, empresa } = useSessao();
  const [lista, setLista] = useState<Autorizacao[] | null>(null);
  const [atuadores, setAtuadores] = useState<string[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [form, setForm] = useState({ atuador: "", acao: "DESLIGAR_BOMBA", motivo: "", validade_dias: 30 });
  const [aberta, setAberta] = useState<{ id: string; execucoes: Execucao[] } | null>(null);
  const carregar = useCallback(() => {
    if (!empresa) return;
    api<Autorizacao[]>(`/v1/manobras/automaticas?empresa=${empresa}&todas=true`).then(setLista).catch(() => setLista([]));
  }, [empresa]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    if (empresa) api<{ device_id: string }[]>(`/v1/admin/dispositivos?empresa=${empresa}&situacao=ativo`).then((l) => setAtuadores(l.map((d) => d.device_id))).catch(() => setAtuadores([]));
  }, [empresa]);
  if (!usuario || !empresa) return null;
  const autoriza = pode.autorizarManobraAutomatica(usuario);
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(ok); carregar(); }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };
  const conceder = (e: FormEvent) => {
    e.preventDefault();
    void executar(() => api("/v1/manobras/automaticas", { corpo: { empresa, ...form } }), `Autorizado: ${form.acao} em ${form.atuador} por ${form.validade_dias} dia(s).`)
      .then(() => setForm({ ...form, motivo: "" }));
  };
  const verExecucoes = async (id: string) => setAberta({ id, execucoes: await api<Execucao[]>(`/v1/manobras/automaticas/${id}/execucoes`) });
  return (
    <section className="admin">
      <header className="linha-titulo"><h2 className="nx-titulo">{NOMES.manobraAutomatica}</h2></header>
      <p className="nx-suave">O motor só executa uma manobra automática de regra com autorização vigente de supervisor ou coordenador da operadora,
        sempre pelas checagens de pré-execução. A equipe {marcaAtual().empresa} nunca autoriza nem aciona equipamento.</p>
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      {autoriza && (
        <form className="form-filial nx-cartao" onSubmit={conceder} aria-label="Conceder autorização">
          <div className="nx-campo"><label htmlFor="ma-atuador">Equipamento (atuador)</label>
            <select id="ma-atuador" required value={form.atuador} onChange={(e) => setForm({ ...form, atuador: e.target.value })}>
              <option value="">—</option>{atuadores.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
          <div className="nx-campo"><label htmlFor="ma-acao">Ação</label>
            <select id="ma-acao" value={form.acao} onChange={(e) => setForm({ ...form, acao: e.target.value })}>
              {ACOES.map((a) => <option key={a} value={a}>{a.replace("_", " ").toLowerCase()}</option>)}</select></div>
          <div className="nx-campo"><label htmlFor="ma-validade">Validade</label>
            <select id="ma-validade" value={form.validade_dias} onChange={(e) => setForm({ ...form, validade_dias: Number(e.target.value) })}>
              <option value={7}>7 dias</option><option value={30}>30 dias</option><option value={90}>90 dias</option><option value={365}>1 ano</option></select></div>
          <div className="nx-campo campo-largo"><label htmlFor="ma-motivo">Motivo</label>
            <input id="ma-motivo" required minLength={5} maxLength={500} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              placeholder="ex.: desligar a bomba da EEE em pressão crítica no setor" /></div>
          <button type="submit" className="nx-botao nx-botao-principal">Conceder autorização</button>
        </form>
      )}
      {!lista && <p className="nx-suave">carregando…</p>}
      {lista && (
        <div className="tabela-rolagem">
          <table className="tabela tabela-usuarios">
            <thead><tr><th>Equipamento</th><th>Ação</th><th>Situação</th><th>Validade</th><th>Autorizado por</th><th>Regras</th><th>Execuções</th>{autoriza && <th />}</tr></thead>
            <tbody>
              {lista.map((a) => (
                <tr key={a.id}>
                  <td className="nx-valor">{a.atuador}</td><td>{a.acao}</td>
                  <td><span className={`nx-selo ${SITUACAO[a.situacao][1]}`}>{SITUACAO[a.situacao][0]}</span>
                    {a.situacao === "revogada" && <small className="nx-suave"> por {a.revogado_por}</small>}</td>
                  <td>{a.valido_ate ? quando(a.valido_ate) : "sem prazo"}</td>
                  <td>{a.autorizado_por}<br /><small className="nx-suave">{quando(a.criado_em)} · {a.motivo}</small></td>
                  <td>{a.regras.length ? a.regras.map((r) => <div key={r.id}><Link to={`/regras/${r.id}`}>{r.nome}</Link>{!r.ativa && <small className="nx-suave"> (desativada)</small>}</div>)
                    : <small className="nx-suave">nenhuma regra usa</small>}</td>
                  <td>{a.execucoes ? <button type="button" className="nx-link" onClick={() => void verExecucoes(a.id)}>{a.execucoes}</button> : 0}</td>
                  {autoriza && <td>{a.situacao === "vigente" && <button type="button" className="nx-botao"
                    onClick={() => void executar(() => api(`/v1/manobras/automaticas/${a.id}/revogar`, { corpo: {} }), "Autorização revogada: a próxima manobra automática já não acontece.")}>Revogar</button>}</td>}
                </tr>
              ))}
              {!lista.length && <tr><td colSpan={8} className="nx-suave">nenhuma autorização</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {aberta && (
        <div className="nx-cartao aviso-admin" aria-label="Execuções automáticas">
          <b>Execuções automáticas</b> <button type="button" className="nx-link" onClick={() => setAberta(null)}>fechar</button>
          <ul>{aberta.execucoes.map((x) => <li key={x.command_id}>{quando(x.criado_em)} · {x.status}{x.motivo ? ` · ${x.motivo}` : ""}</li>)}</ul>
        </div>
      )}
    </section>
  );
}
